//! Importar dependências
import { API_URI } from "../../constants/consts";
import { api } from "../../services/apis/configs/axiosConfigs";

//* Type para dados de paginação
import { type Pagination } from "../../services/stores/types";

//* Importar tipos de QuestionTag
import {
  type QuestionTag,
  type GetQuestionTagsResponse,
  type CreateQuestionTagsInput,
} from "./types/types";

/**
 * QuestionTagAPI é um objeto utilitário para gerenciar operações CRUD (criar, ler, atualizar, deletar)
 * para associações entre perguntas e tags através de requisições HTTP.
 */
export const questionTagAPI = {
  /**
   * Busca uma lista paginada de associações entre perguntas e tags.
   *
   * @param {string} search - Termo de busca opcional para filtrar associações.
   * @param {number} page - Número da página para paginação (padrão é 1).
   * @param {string} token - Token de autenticação para os headers da requisição.
   * @param {number} perPage - Número de itens por página (padrão é 10).
   * @param {string} [questionId] - ID da pergunta para filtrar as associações.
   *
   * @returns {Promise<GetQuestionTagsResponse>} - Retorna uma resposta paginada contendo associações e informações de paginação.
   */
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10,
    questionId?: string
  ): Promise<GetQuestionTagsResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const questionFilter = questionId ? `&questionId=${questionId}` : "";
    const url = `${API_URI}/admin/private/question-tags?page=${page}&perPage=${perPage}${searchQuery}${questionFilter}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          questionTags: data.question_tags || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching question tags:", error);
        throw new Error("Failed to fetch question tags");
      });
  },

  /**
   * Busca uma associação específica entre pergunta e tag pelo seu ID.
   *
   * @param {string} id - UUID da associação a ser buscada.
   * @param {string} token - Token de autenticação para os headers da requisição.
   *
   * @returns {Promise<QuestionTag>} - Retorna os dados da associação se encontrada.
   *
   * @throws {Error} - Lança um erro se a associação não for encontrada ou se a resposta for inválida.
   */
  getById: async function (id: string, token: string): Promise<QuestionTag> {
    const url = `${API_URI}/admin/private/question-tags/${id}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        if (response.data && response.data.result) {
          return response.data.data;
        } else {
          throw new Error("QuestionTag not found or invalid response.");
        }
      })
      .catch((error) => {
        console.error("Error fetching question tag:", error);
        throw new Error("Failed to fetch question tag");
      });
  },

  /**
   * Cria novas associações entre perguntas e tags enviando uma requisição POST para a API.
   *
   * @param {CreateQuestionTagInput[]} data - Array de dados para as novas associações.
   * @param {string} token - Token de autenticação para os headers da requisição.
   *
   * @returns {Promise<QuestionTag[]>} - Retorna os dados das associações criadas.
   *
   * @throws {Error} - Lança um erro se a criação das associações falhar.
   */
  create: async function (
    data: CreateQuestionTagsInput,
    token: string
  ): Promise<QuestionTag[]> {
    const url = `${API_URI}/admin/private/question-tags`;

    return api
      .request({
        url,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error(
          "Erro ao criar associações de tags:",
          error.response?.data || error
        );
        throw new Error("Falha ao criar associações de tags");
      });
  },


  /**
   * Deleta uma ou mais associações entre perguntas e tags enviando uma requisição DELETE para a API.
   *
   * @param {string[]} ids - Array de UUIDs das associações a serem deletadas.
   * @param {string} token - Token de autenticação para os headers da requisição.
   *
   * @returns {Promise<void>} - Retorna uma resposta de sucesso se a deleção for bem-sucedida.
   *
   * @throws {Error} - Lança um erro se a deleção falhar.
   */
  delete: async function (ids: string[], token: string): Promise<void> {
    const url = `${API_URI}/admin/private/question-tags`;

    return api
      .request({
        url,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { ids },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting question tags:", error);
        throw new Error("Failed to delete question tags");
      });
  },
};
